import * as THREE from 'three';
import {
  Shared, Config, events, Camera, defines, SystemEnv, OrbitCameraControl,
} from '../base';
import {
  webcadDefines, ThreeView, webcadcamera
} from './index';
import * as webcad from '../../webcad';

// マテリアルタイプ
export enum ItemType {
    Entry = 0,
    Door = 1,
    Sash = 2,
    Curtain = 3,
    Floor = 4,
    Cloth = 5,
    Roof = 6,
    Wall = 7,
    WallKosi = 8,
    Garage = 9,
    Habaki = 10,

}
export enum TargetType {
    Mesh = 0,
    Line = 1,
    HitMesh = 2,
    Furniture = 3,
    Max = 4,
}

// カメラ情報など.
export class CameraState {
    private _pos: THREE.Vector3;

    private _dir: THREE.Vector3;

    private _floor: number;

    constructor() {
      this._pos = new THREE.Vector3(0, 0, 0);
      this._dir = new THREE.Vector3(0, 0, 0);
      this._floor = -1; // unknown;
    }

    set position(pos: { x: number, y: number, z: number }) {
      this._pos.set(pos.x, pos.y, pos.z);
    }

    get position(): { x: number, y: number, z: number } {
      return { x: this._pos.x, y: this._pos.y, z: this._pos.z };
    }

    set direction(dir: { x: number, y: number, z: number }) {
      this._dir.set(dir.x, dir.y, dir.z);
    }

    get direction(): { x: number, y: number, z: number } {
      return { x: this._dir.x, y: this._dir.y, z: this._dir.z };
    }

    get floor(): number { return this._floor; }

    set floor(val: number) { this._floor = val; }
}

// アイテム情報
export class w3dItemInfo {
    private _name: string;

    private _description: string;

    private _url: string;

    private _color: string;

    private _selected: boolean;

    private _thumbnail: string;

    private _category: string;

    constructor(param: { name: string, url?: string, color?: string, desc?: string, selected?: boolean, thumbnail?:string, category?:string }) {
      this._name = param.name;
      this._url = param.url;
      this._color = (param.color != undefined) ? param.color : undefined;
      this._description = (param.desc != undefined) ? param.desc : '';
      this._selected = (param.selected != undefined) ? param.selected : false;
      this._thumbnail = (param.thumbnail != undefined) ? param.thumbnail : '';
      this._category = (param.category != undefined) ? param.category : '';
    }

    public get name(): string { return this._name; }

    public get url(): string { return this._url; }

    public get color(): string { return this._color; }

    public get description(): string { return this._description; }

    public get selected(): boolean { return this._selected; }

    public get thumbnail(): string { return this._thumbnail; }

    public get category(): string { return this._category; }
}

// アイテム変更情報
export class w3dItemChangeInfo {
    private _index: number;

    private _target: any;

    private _alternatives: any[];

    private _dir: string;

    constructor(index: number, target: any, alternatives: any[], dir: string) {
      this._index = index;
      this._target = target;
      this._alternatives = alternatives;
      this._dir = dir;
    }

    public get index(): any { return this._index; }

    public get target(): any { return this._target; }

    public get alternatives(): any[] { return this._alternatives; }

    public get dir(): string { return this._dir; }
}

/* SharedManagers
 * webcad2Dとの連絡用.
 */
export class SharedManagers extends Shared {
  public get xmlData(): HTMLElement { return this._xmlData; }

  public get fitData(): any[] { return this._fitData; }

  public get equData(): any[] { return this._equData; }

  public get furData(): any[] { return this._furData; }

  public get extData(): any[] { return this._extData; }

  public get staData(): any[] { return this._staData; }

  public get ligData(): any[] { return this._ligData; }

  public get decData(): any[] { return this._decData; }

  public get local(): boolean { return this._local_use; }

  public set local(val: boolean) {
    this._local_use = val;
    this._base_url = (this._local_use ? this._local_base_url : this._remote_base_url);
  }

  /* reset
     * データのリセット.
     */
  public reset() {
    this._xmlData = null;
    this._fitData = [];
    this._equData = [];
    this._furData = [];
    this._extData = [];
    this._staData = [];
    this._ligData = [];
    this._decData = [];
    this.setAllItemVisible();
  }

  // 各種URLの取得.
  // ベースURL.
  public get baseURL(): string { return this._base_url; }

  // 3dフォルダのURL.
  public get threedDir(): string { return this._subpaths[webcadDefines.GlobalDefs.threedDirName]; }

  // アイテムフォルダのURL.
  public get itemDir(): string { return this._subpaths[webcadDefines.GlobalDefs.itemDirName]; }

  // テクスチャフォルダのURL.
  public get tex3dDir(): string { return this._subpaths[webcadDefines.GlobalDefs.text3DBaseDirName]; }

  // アイコンリソースのURL.
  public get libsDir(): string { return this._subpaths[webcadDefines.GlobalDefs.libsDirName]; }

  // アイテム定義ファイルのURL.
  public get clrDefPath(): string { return this._subpaths[webcadDefines.GlobalDefs.clrDefPathName]; }

  // マテリアル定義ファイルのURL.
  public get matDefPath(): string { return this._subpaths[webcadDefines.GlobalDefs.matDefPathName]; }

  // デフォルトのマテリアル定義ファイルのURL.
  public get styDefPath(): string { return this._subpaths[webcadDefines.GlobalDefs.stylDefPathName]; }

  /* eventDispatcher
     * eventDispatcher位置を返す.
     */
  public get eventDispatcher(): events.EventDispatcher {
    return ThreeView.get().eventDispatcher;
  }

  /* cameraState
     * ウォークスルーのカメラ位置を返す.
     */
  public get cameraState(): CameraState {
    const state: CameraState = new CameraState();
    const cams: webcadcamera.WebCadCameras = ThreeView.get().wcCameras;
    const cam: Camera = cams.getCamera(defines.CameraDefs.wlkthCamNo);
    const pos = cam.position;
    const dir = cam.lookAt;
    state.position = { x: pos.x, y: pos.y, z: pos.z };
    state.direction = { x: dir.x, y: dir.y, z: dir.z };
    state.floor = cams.floor;
    return state;
  }

  /* cameraState
     * ウォークスルーのカメラ位置を設定する.
     */
  public set cameraState(state: CameraState) {
    const cams: webcadcamera.WebCadCameras = ThreeView.get().wcCameras;
    const cam: Camera = cams.getCamera(defines.CameraDefs.wlkthCamNo);
    const dir: THREE.Vector3 = new THREE.Vector3(state.direction.x, state.direction.y, state.direction.z);
    cam.position.set(0, 0, 0);
    cam.lookAt = dir;
    cam.position.set(state.position.x, state.position.y, state.position.z);
    cams.floor = state.floor;
    cam.notify(defines.EventNames.cameraReset, {});
    cams.notifyChange(defines.CameraDefs.wlkthCamNo);
    this.eventDispatcher.dispatchEvent(new events.Event(webcadDefines.WebCadEventNames.wtFloorChanged, state.floor));
  }

  public saveCameraState() {
    if (ThreeView.get().wcCameras.no == defines.CameraDefs.wlkthCamNo) {
        this._savedCamState = this.cameraState;
    } else {
        this._savedCamState = undefined;
    }
  }

  public canRestoreCameraState(): boolean {
    return this._savedCamState != undefined;
  }

  public restoreCameraState() {
    if (!this.canRestoreCameraState()) return;
    this.cameraState = this._savedCamState;
  }

  public clearCameraState() {
    this._savedCamState = undefined;
    this.keepCameraState = false;
  }

  public set keepCameraState(value: boolean) {
    this._keepCameraState = value;
  }

  public get keepCameraState(): boolean {
    return this._keepCameraState;
  }

  /* setCameraFromMiniPt
     * MiniMapマップポイントからカメラ位置を設定する.
     */
  setCameraFromMiniPt(pt: { x: number, y: number }) {
    ThreeView.get().wcItemmgr.setCameraFromMiniPt(new THREE.Vector2(pt.x, pt.y));
  }

  /* getMaterialList
     * 入替可能なマテリアルのリスト.
     */
  public getMaterialList(type: ItemType, floor?: number): w3dItemInfo[] {
    return ThreeView.get().matmgr.getAlternativeList(type, floor);
  }

  /* getItemList
     * 変更可能なアイテムのリスト.
     */
//   public getItemList(id: number): w3dItemChangeInfo {
//     return ThreeView.get().wcItemmgr.getAlternativeList(id);
//   }

  /* show3D
     * 3D表示開始.
     */
  public parseXML(xmlString: string) {
    ThreeView.get().planmgr.perseXML(xmlString);
  }

  /* homeCamera
     * カメラをホームポジションへ移動.
     */
  public homeCamera() {
    ThreeView.get().cameras.home();
  }

  /* getPartsInfo
     * 入替可能なマテリアルのリスト.
     */
  public getPartsInfo(type: string): {} {
    const plan = ThreeView.get().planmgr.autoData;
    if (plan == undefined) return undefined;
    return plan.autoPartsInfo(type);
  }

  /* changeWallColor
     * 壁紙を変更する.
     */
  public changeWallColor(index: number) {
    ThreeView.get().leftMenuMaterial.changeColor(ItemType.Cloth, index);

    // 変更フラグを立てる
    webcad.WebCADGlobal.webcad2Dmanager.isChanged = true;
  }

  /* changeFloorColor
     * 床を変更する.
     */
  public changeFloorColor(index: number) {
    ThreeView.get().leftMenuMaterial.changeColor(ItemType.Floor, index);

    // 変更フラグを立てる
    webcad.WebCADGlobal.webcad2Dmanager.isChanged = true;
  }

  /* changeHabakiColor
   * 巾木を変更する.
   */
  public changeHabakiColor(index: number) {
    ThreeView.get().leftMenuMaterial.changeColor(ItemType.Habaki, index);

    // 変更フラグを立てる
    webcad.WebCADGlobal.webcad2Dmanager.isChanged = true;
  }


  /* changeRoofColor
     * 屋根を変更する.
     */
  public changeRoofColor(index: number) {
    ThreeView.get().leftMenuMaterial.changeColor(ItemType.Roof, index);

    // 変更フラグを立てる
    webcad.WebCADGlobal.webcad2Dmanager.isChanged = true;
  }

  /* changeOutWallColor
     * 外壁を変更する.
     */
  public changeOutWallColor(floor: number, index: number) {
    ThreeView.get().leftMenuMaterial.changeColor(ItemType.Wall, index, floor);

    // 変更フラグを立てる
    webcad.WebCADGlobal.webcad2Dmanager.isChanged = true;
  }

  /* changeOutWallKosiColor
     * 外壁腰を変更する.
     */
  public changeOutWallKosiColor(floor: number, index: number) {
    ThreeView.get().leftMenuMaterial.changeColor(ItemType.WallKosi, index, floor);

    // 変更フラグを立てる
    webcad.WebCADGlobal.webcad2Dmanager.isChanged = true;
  }

  /* changeItemColor
     * 建材の色を変更する.
     */
  public changeItemColor(type: ItemType, index: number) {
    ThreeView.get().leftMenuMaterial.changeColor(type, index);

    // 変更フラグを立てる
    webcad.WebCADGlobal.webcad2Dmanager.isChanged = true;
  }

  /**
     * アイテムの表示/非表示.
     * @param part
     * @param value
     */
  public setItemDisplay(floor?: number) {
    ThreeView.get().wcItemmgr.setHouseObjectVisible();

    if (ThreeView.get().wcItemmgr.wireframe || !ThreeView.get().wcItemmgr.houseObjects.visible) {
      ThreeView.get().wcItemmgr.wireframe = false;
      ThreeView.get().wcItemmgr.wireframe = true;
    }
    ThreeView.get().menus.setItemDisplayButtonStatus();
  }

    // 表示/非表示メニューの項目リスト
    private _partTypeList = [
      {
        id: webcadDefines.PartType.roof, en: 'Roof', jp: '屋根', visible: true,
      },
      {
        id: webcadDefines.PartType.ceiling, en: 'Ceiling', jp: '天井', visible: true,
      },
      {
        id: webcadDefines.PartType.wall, en: 'Wall', jp: '壁', visible: true,
      },
      {
        id: webcadDefines.PartType.floor, en: 'Floor', jp: '床', visible: true,
      },
      {
        id: webcadDefines.PartType.sash, en: 'Sash', jp: 'サッシ', visible: true,
      },
      {
        id: webcadDefines.PartType.fitting, en: 'Fittings', jp: '建具', visible: true,
      },
      {
        id: webcadDefines.PartType.equipment, en: 'Equipment', jp: '住宅設備', visible: true,
      },
      {
        id: webcadDefines.PartType.furniture, en: 'Furniture', jp: '家具', visible: true,
      },
      {
        id: webcadDefines.PartType.exterior, en: 'Exterior', jp: 'エクステリア', visible: true,
      },
      {
        id: webcadDefines.PartType.site, en: 'Site', jp: '敷地', visible: true,
      },
    ];

    public get partType() {
      return this._partTypeList;
    }

    public set partType(partType) {
      this._partTypeList = partType;
    }

    private setAllItemVisible() {
      for (let i = 0, len = this._partTypeList.length; i < len; i++) {
        this._partTypeList[i].visible = true;
      }
    }

    public isAllItemVisible(): boolean {
      let visible = true;
      for (let i = 0, len = this._partTypeList.length; i < len; i++) {
        if (!this._partTypeList[i].visible) {
          visible = false;
          break;
        }
      }
      return visible;
    }

    /* nighttime
     * 昼夜の切替.
     */
    public get nighttime(): boolean {
      const hour = ThreeView.get().wcItemmgr.getTime();
      return (hour > 18) || (hour < 6);
    }

    /* night
     * 昼夜の切替.
     */
    public set nighttime(value: boolean) {
      if (value) ThreeView.get().toNightTime();
      else ThreeView.get().toDayTime();
    }

    /* rtcam
     */
    public get rtcam(): boolean {
      return false;
    }

    /* rtcam
     */
    public set rtcam(value: boolean) {
      ;
    }

    /* enhanced
     */
    public get enhanced(): boolean {
      return ThreeView.get().renderer.posteffect;
    }

    /* enhanced
     */
    public set enhanced(value: boolean) {
      ThreeView.get().renderer.posteffect = value;
    }

    /* go
     */
    public go(duration?: number) {
      ThreeView.get().cameras.command(defines.Commands.go, (duration == undefined ? {} : { timeout: duration }));
    }

    /* stop
     */
    public stop() {
      ThreeView.get().cameras.command(defines.Commands.stop);
    }

    /* back
     */
    public back(duration?: number) {
      ThreeView.get().cameras.command(defines.Commands.back, (duration == undefined ? {} : { timeout: duration }));
    }

    /* updateMenus
     */
    public updateMenus() {
      ThreeView.get().menus.adjustMenus();
    }

    /* setback
     * 斜線規制の表示/非表示.
     */
    public get setback(): boolean {
      return ThreeView.get().wcItemmgr.setbackObjects.visible;
    }

    /* setback
     * 斜線規制の表示/非表示.
     */
    public set setback(value: boolean) {
      ThreeView.get().wcItemmgr.setbackObjects.visible = value;
    }

    /* getTargetObjects
     * 衝突判定などで使うメッシュの取得.
     */
    public getTargetObjects(types: number): any[] {
      const objs: any = [];
      for (let i = 0; i < TargetType.Max; i++) {
        if (types & (1 << i)) {
          switch (i) {
            case TargetType.Mesh: Array.prototype.push.apply(objs, ThreeView.get().wcItemmgr.meshs); break;
            case TargetType.Line: Array.prototype.push.apply(objs, ThreeView.get().wcItemmgr.lines); break;
            case TargetType.HitMesh: Array.prototype.push.apply(objs, ThreeView.get().wcItemmgr.hitMeshs); break;
            case TargetType.Furniture: Array.prototype.push.apply(objs, ThreeView.get().wcItemmgr.furnitureObjects); break;
          }
        }
      }
      return objs;
    }

    /* startLoadingTexture
     */
    public startLoadingTexture(url: string) {
      ThreeView.get().wcItemmgr.startLoadingTexture(url);
    }

    /* endLoadingTexture
     */
    public endLoadingTexture(url: string) {
      ThreeView.get().wcItemmgr.endLoadingTexture(url);
    }

    /* errorLoadingTexture
     */
    public errorLoadingTexture(message: string) {
      ThreeView.get().wcItemmgr.errorLoadingTexture(message);
    }

    // 内部使用.
    private _local_use: boolean;

    private _base_url: string;

    private _local_base_url: string;

    private _remote_base_url: string;

    private _subpaths: { [key: string]: string };

    private _xmlData: HTMLElement;

    private _fitData: any;

    private _equData: any;

    private _furData: any;

    private _extData: any;

    private _staData: any;

    private _ligData: any;

    private _decData: any;

    private _savedCamState: CameraState;

    private _keepCameraState: boolean = false;

    public static get(params?: {}): any {
      if ((Shared._instance === undefined) && (params != undefined)) {
        new SharedManagers(params);
      }
      return Shared._instance;
    }

    constructor(params: any) {
      super(params);

      this._local_base_url = params[webcadDefines.GlobalDefs.localBaseUrlName];
      this._remote_base_url = params[webcadDefines.GlobalDefs.remoteBaseUrlName];
      this.local = SystemEnv.islocalhost;

      this._subpaths = {};
      this._subpaths[webcadDefines.GlobalDefs.threedDirName] = params[webcadDefines.GlobalDefs.threedDirName];
      this._subpaths[webcadDefines.GlobalDefs.itemDirName] = params[webcadDefines.GlobalDefs.itemDirName];
      this._subpaths[webcadDefines.GlobalDefs.text3DBaseDirName] = params[webcadDefines.GlobalDefs.text3DBaseDirName];
      this._subpaths[webcadDefines.GlobalDefs.animDataDirName] = params[webcadDefines.GlobalDefs.animDataDirName];
      this._subpaths[webcadDefines.GlobalDefs.libsDirName] = params[webcadDefines.GlobalDefs.libsDirName];
      this._subpaths[webcadDefines.GlobalDefs.clrDefPathName] = params[webcadDefines.GlobalDefs.clrDefPathName];
      this._subpaths[webcadDefines.GlobalDefs.matDefPathName] = params[webcadDefines.GlobalDefs.matDefPathName];
      this._subpaths[webcadDefines.GlobalDefs.stylDefPathName] = params[webcadDefines.GlobalDefs.stylDefPathName];

      this.reset();
    }

    /* getURL
     * 指定のURLを返す.
     */
    public getUrl(id: string): string {
      if (id == defines.UrlDefs.iconFolder) return `${this.baseURL}assets/`;
      return super.getUrl(id);
    }

    /* getLang
     * 言語を返す.
     */
    public getLang(): string {
      if (webcad.WebCADGlobal.language == undefined) return 'ja';
      return webcad.WebCADGlobal.language == 'jp' ? 'ja' : webcad.WebCADGlobal.language;
    }
}
